import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark';

interface ThemeState {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

/**
 * Store del tema. Persistido en localStorage para mantener la preferencia
 * del usuario entre sesiones.
 *
 * Default: 'dark'. El usuario puede cambiarlo desde el header del layout y
 * la preferencia queda guardada en localStorage bajo la clave 'mdm-theme'.
 *
 * Cuando cambia el modo, se actualiza el atributo data-theme del <html>,
 * lo que activa las CSS variables del modo oscuro definidas en theme.css.
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'dark',

      setMode: (mode) => {
        set({ mode });
        applyThemeToDom(mode);
      },

      toggle: () => {
        const newMode = get().mode === 'light' ? 'dark' : 'light';
        set({ mode: newMode });
        applyThemeToDom(newMode);
      },
    }),
    {
      name: 'mdm-theme',
      onRehydrateStorage: () => (state) => {
        // Al cargar desde localStorage, aplicar al DOM
        if (state) {
          applyThemeToDom(state.mode);
        }
      },
    },
  ),
);

// Aplicar el tema default ('dark') al DOM apenas se carga el modulo,
// antes de que React monte. Esto evita un flash de tema claro en el primer paint
// para usuarios nuevos sin localStorage previo.
if (typeof document !== 'undefined') {
  const stored = localStorage.getItem('mdm-theme');
  let initialMode: ThemeMode = 'dark';
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      if (parsed?.state?.mode === 'light' || parsed?.state?.mode === 'dark') {
        initialMode = parsed.state.mode;
      }
    } catch {
      // Si el localStorage esta corrupto, usar default.
    }
  }
  document.documentElement.setAttribute('data-theme', initialMode);
}

function applyThemeToDom(mode: ThemeMode) {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', mode);
  }
}
